import { useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, CircularProgress, Grid, IconButton, MenuItem, Stack, TablePagination, TextField, Tooltip, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { getSpaCommunicationHistory } from '../APIs/SpaAPI';
import { COMMUNICATION_CHANNELS, COMMUNICATION_EVENTS, COMMUNICATION_STATUSES } from '../utils/spaCommunications';
import SpaCommunicationSendDialog from './SpaCommunicationSendDialog';
import SpaCommunicationDeliveries from './SpaCommunicationDeliveries';

const blank = { clientId: '', appointmentId: '', waitlistOfferId: '', storedValueInstrumentId: '', packageEntitlementId: '', event: '', channel: '', status: '' };

export default function SpaCommunicationHistory({ token, tenant, canSend, capabilities }) {
  const { t } = useTranslation();
  const ct = (key, options) => t(`spa.communications.${key}`, options);
  const [filters, setFilters] = useState(blank);
  const [draft, setDraft] = useState(blank);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [reload, setReload] = useState(0);
  const [state, setState] = useState(null);
  const [sending, setSending] = useState(false);
  const scope = JSON.stringify([token, tenant, filters, page, pageSize]);
  const current = state?.scope === scope ? state : null;
  const query = Object.fromEntries(Object.entries(filters).filter(([, value]) => value !== ''));
  const validFilters = Object.keys(blank).filter((key) => key.endsWith('Id')).every((key) => !draft[key]
    || (Number.isSafeInteger(Number(draft[key])) && Number(draft[key]) > 0));
  const refresh = () => setReload((value) => value + 1);
  useEffect(() => {
    const controller = new AbortController();
    setState((previous) => ({ ...(previous?.scope === scope ? previous : {}), scope, loading: true, error: '' }));
    const [session, tenantId, selected, pageIndex, size] = JSON.parse(scope);
    const requested = Object.fromEntries(Object.entries(selected).filter(([, value]) => value !== ''));
    getSpaCommunicationHistory({ ...requested, page: pageIndex + 1, pageSize: size }, session, tenantId, { signal: controller.signal }).then((data) => {
      if (!data || !Array.isArray(data.items) || data.items.some((item) => !item || item.id == null || typeof item.status !== 'string')
        || !Number.isInteger(data.total) || data.total < 0 || data.page !== pageIndex + 1 || data.pageSize !== size) {
        throw new Error(t('spa.communications.invalidResponse'));
      }
      if (!controller.signal.aborted) setState({ scope, data });
    }).catch((error) => { if (!controller.signal.aborted) setState((previous) => ({ ...(previous?.scope === scope ? previous : {}), scope, loading: false, error: error.message })); });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, reload]);
  useEffect(() => {
    const refreshVisible = () => { if (!document.hidden && !sending) setReload((value) => value + 1); };
    const timer = window.setInterval(refreshVisible, 30000);
    window.addEventListener('focus', refreshVisible);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refreshVisible); };
  }, [sending]);
  return <Stack spacing={2}>
    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} alignItems={{ sm: 'center' }}>
      <Typography component="h2" variant="h6">{ct('historyTitle')}</Typography>
      <Stack direction="row" spacing={1} alignItems="center">
        <Tooltip title={ct('refresh')}><span><IconButton aria-label={ct('refresh')} disabled={current?.loading} onClick={refresh}><RefreshIcon /></IconButton></span></Tooltip>
        {canSend && <Button variant="contained" startIcon={<SendIcon />} disabled={!capabilities} onClick={() => setSending(true)}>{ct('newCommunication')}</Button>}
      </Stack>
    </Stack>
    <Box component="form" onSubmit={(event) => { event.preventDefault(); if (validFilters) { setFilters(draft); setPage(0); refresh(); } }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField fullWidth size="small" type="number" label={ct('clientId')} value={draft.clientId}
            inputProps={{ min: 1, step: 1 }} onChange={(event) => setDraft((previous) => ({ ...previous, clientId: event.target.value }))} />
        </Grid>
        {Object.entries({ event: COMMUNICATION_EVENTS, channel: COMMUNICATION_CHANNELS, status: COMMUNICATION_STATUSES }).map(([key, values]) =>
          <Grid item xs={12} sm={6} md={3} key={key}>
            <TextField select fullWidth size="small" label={ct(key === 'status' ? 'deliveryStatus' : key)} value={draft[key]}
              onChange={(event) => setDraft((previous) => ({ ...previous, [key]: event.target.value }))}>
              <MenuItem value="">{ct(key === 'status' ? 'allStatuses' : 'all')}</MenuItem>
              {values.map((value) => <MenuItem key={value} value={value}>{ct(`${key === 'status' ? 'statuses' : key + 's'}.${value}`)}</MenuItem>)}
            </TextField>
          </Grid>)}
      </Grid>
      <Accordion variant="outlined" disableGutters elevation={0} sx={{ mt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{ct('recordFilters')}</AccordionSummary>
        <AccordionDetails><Grid container spacing={2}>
          {Object.keys(blank).filter((key) => key.endsWith('Id') && key !== 'clientId').map((key) => <Grid item xs={12} sm={6} key={key}>
            <TextField fullWidth size="small" type="number" label={ct(`targets.${key}`)} value={draft[key]} inputProps={{ min: 1, step: 1 }}
              onChange={(event) => setDraft((previous) => ({ ...previous, [key]: event.target.value }))} />
          </Grid>)}
        </Grid></AccordionDetails>
      </Accordion>
      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button type="submit" variant="outlined" disabled={!validFilters}>{ct('applyFilters')}</Button>
        <Button startIcon={<FilterAltOffIcon />} onClick={() => { setDraft(blank); setFilters(blank); setPage(0); refresh(); }}>{ct('clearFilters')}</Button>
      </Stack>
    </Box>
    {current?.error && <Alert severity="error" action={<Button color="inherit" onClick={refresh}>{ct('retry')}</Button>}>{current.error}</Alert>}
    {(!current || current.loading) && <CircularProgress size={22} aria-label={ct('historyLoading')} />}
    {current?.data && <>
      {!current.data.items.length && <Typography color="text.secondary">{ct('historyEmpty')}</Typography>}
      <SpaCommunicationDeliveries items={current.data.items} token={token} tenant={tenant} canSend={canSend} capabilities={capabilities} onChange={refresh} />
      <TablePagination component="div" count={current.data.total} page={page} rowsPerPage={pageSize} rowsPerPageOptions={[10, 25, 50, 100]}
        onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }}
        labelRowsPerPage={ct('rowsPerPage')} labelDisplayedRows={({ from, to, count }) => ct('pageRange', { from, to, count })}
        getItemAriaLabel={(type) => ct(`pagination.${type}`)} sx={{ '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', px: 0 }, '& .MuiTablePagination-spacer': { display: { xs: 'none', sm: 'block' } } }} />
    </>}
    {sending && canSend && <SpaCommunicationSendDialog open token={token} tenant={tenant} capabilities={capabilities} onClose={() => setSending(false)} onResult={refresh} />}
    {Object.keys(query).length > 0 && <Typography variant="caption" color="text.secondary">{ct('filteredHistory')}</Typography>}
  </Stack>;
}
